import { verboseDebug } from 'classes/helpers/debug.helper';
import { getFireState } from 'classes/helpers/fire-state.helper';
import { AimingContext } from 'contexts/aiming.context';
import { FiringContext } from 'contexts/firing/context';
import { MachineContext } from 'contexts/machine.context';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { IVerboseComponent } from 'interfaces/i-components';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useContext, useEffect } from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';

// returns the value from the previous render (or undefined on first render)
// handy for when you need to compare the current value to the previous value to see if it changed
const useFiringHeartbeat = (
  props: IVerboseComponent & {
    trainingShots: IMachineShot[] | undefined;
  }
) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  const fireStateDates = useMachineStatusStore((s) => s.fireStateDates);
  const queueState = useMachineStatusStore((s) => s.queueState);
  const s2mReady = useMachineStatusStore((s) => s.s2mReady);

  const { autoFire, lastBallCount } = useContext(MachineContext);

  const {
    isTraining,
    ignoreTR,
    requiresSend,
    sendingUntil,
    pitchBP,
    pitchTraining,
  } = useContext(AimingContext);

  const { fireState, ignoreAf, suspended, setFireState } =
    useContext(FiringContext);

  const latestAF = useLatest(autoFire);
  const latestBallCount = useLatest(lastBallCount);
  const latestFireState = useLatest(fireState);
  const latestFireStateDates = useLatest(fireStateDates);
  const latestIgnoreAf = useLatest(ignoreAf);
  const latestPitchBP = useLatest(pitchBP);
  const latestPitchTraining = useLatest(pitchTraining);
  const latestQueueState = useLatest(queueState);
  const latestRequiresSend = useLatest(requiresSend);
  const latestS2mReady = useLatest(s2mReady);
  const latestSendingUntil = useLatest(sendingUntil);
  const latestSuspended = useLatest(suspended);
  const latestTrainingShots = useLatest(props.trainingShots);

  // re-evaluate date checks (using the latest values) regardless of whether we get any response from the server
  // e.g. failures to detect should not block R2F indefinitely
  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      const nextFireState = getFireState({
        queueState: latestQueueState.current,
        suspended: latestSuspended.current,
        fireStateDates: latestFireStateDates.current,
        sendingUntil: latestSendingUntil.current,
        ballCount: latestBallCount.current,
        isTraining: isTraining,
        ignoreTR: ignoreTR,
        onDemand: onDemand,
        pitchBP: latestPitchBP.current,
        pitchTraining: latestPitchTraining.current,
        requiresSend: latestRequiresSend.current,
        s2mReady: latestS2mReady.current,
        trainingShots: latestTrainingShots.current,
        autoFire: latestAF.current,
        ignoreAf: latestIgnoreAf.current,
      });

      if (nextFireState !== latestFireState.current) {
        verboseDebug(
          props,
          `heartbeat > changing fire state > ${latestFireState.current} to ${nextFireState}`
        );
        setFireState(nextFireState);
      }
    }, 500);

    verboseDebug(props, 'heartbeat > started');

    return () => {
      clearInterval(heartbeatInterval);
      verboseDebug(props, 'heartbeat > cleared');
    };
  }, [onDemand, isTraining, ignoreTR]);
};

export default useFiringHeartbeat;
